/* v05_a24-dawn-refactor/assets/a24-predictive-search.js */
/** Native Shopify predictive search for all theme search fields. */
(function () {
  'use strict';

  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const endpoint = rootPath.replace(/\/?$/, '/') + 'search/suggest.json';
  const normalizeQuery = (query) => window.A24SearchNormalization?.normalize?.(query) || String(query || '').trim();
  const fields = [
    ['navSearchInput', 'navSearchDropdown', 'navSearchWrap'],
    ['searchPageInput', 'searchPageDropdown', 'searchPageWrap'],
    ['heroSearchInput', 'heroSearchDropdown', 'heroSearchWrap'],
    ['drawerSearchInput', 'drawerSearchDropdown', 'drawerSearchWrap']
  ];

  const defaultConfig = {
    enabled: true,
    showQueries: true,
    showProducts: true,
    showCollections: false,
    showPages: false,
    showArticles: false,
    showAllResultsLink: true,
    allResultsTarget: 'search',
    generateQuerySuggestions: true,
    generatedQueryLimit: 5,
    limit: 6,
    limitScope: 'each',
    minChars: 2,
    debounceMs: 220,
    unavailableProducts: 'last',
    fields: {
      title: true,
      productType: true,
      vendor: true,
      variantTitle: true,
      variantSku: false,
      variantBarcode: false,
      tag: false,
      body: false,
      author: false
    }
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const clampInteger = (value, minimum, maximum, fallback) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(Math.max(parsed, minimum), maximum);
  };

  const config = normalizeConfig(window.A24?.predictiveSearch || {});

  function normalizeConfig(rawConfig) {
    const merged = Object.assign({}, defaultConfig, rawConfig || {});
    const rawFields = Object.assign({}, defaultConfig.fields, rawConfig?.fields || {});

    return {
      enabled: typeof merged.enabled === 'boolean' ? merged.enabled : defaultConfig.enabled,
      showQueries: typeof merged.showQueries === 'boolean' ? merged.showQueries : defaultConfig.showQueries,
      showProducts: typeof merged.showProducts === 'boolean' ? merged.showProducts : defaultConfig.showProducts,
      showCollections: typeof merged.showCollections === 'boolean' ? merged.showCollections : defaultConfig.showCollections,
      showPages: typeof merged.showPages === 'boolean' ? merged.showPages : defaultConfig.showPages,
      showArticles: typeof merged.showArticles === 'boolean' ? merged.showArticles : defaultConfig.showArticles,
      showAllResultsLink: typeof merged.showAllResultsLink === 'boolean' ? merged.showAllResultsLink : defaultConfig.showAllResultsLink,
      allResultsTarget: ['search', 'all_products'].includes(merged.allResultsTarget) ? merged.allResultsTarget : defaultConfig.allResultsTarget,
      generateQuerySuggestions: typeof merged.generateQuerySuggestions === 'boolean' ? merged.generateQuerySuggestions : defaultConfig.generateQuerySuggestions,
      generatedQueryLimit: clampInteger(merged.generatedQueryLimit, 1, 10, defaultConfig.generatedQueryLimit),
      limit: clampInteger(merged.limit, 1, 10, defaultConfig.limit),
      limitScope: ['all', 'each'].includes(merged.limitScope) ? merged.limitScope : defaultConfig.limitScope,
      minChars: clampInteger(merged.minChars, 1, 5, defaultConfig.minChars),
      debounceMs: clampInteger(merged.debounceMs, 100, 800, defaultConfig.debounceMs),
      unavailableProducts: ['last', 'hide', 'show'].includes(merged.unavailableProducts) ? merged.unavailableProducts : defaultConfig.unavailableProducts,
      fields: {
        title: rawFields.title !== false,
        productType: rawFields.productType !== false,
        vendor: rawFields.vendor !== false,
        variantTitle: rawFields.variantTitle !== false,
        variantSku: rawFields.variantSku === true,
        variantBarcode: rawFields.variantBarcode === true,
        tag: rawFields.tag === true,
        body: rawFields.body === true,
        author: rawFields.author === true
      }
    };
  }

  function getResourceTypes() {
    const types = [];
    if (config.showQueries) types.push('query');
    if (config.showProducts) types.push('product');
    if (config.showCollections) types.push('collection');
    if (config.showPages) types.push('page');
    if (config.showArticles) types.push('article');
    return types;
  }

  function getSearchFields() {
    const fieldsMap = {
      title: 'title',
      productType: 'product_type',
      vendor: 'vendor',
      variantTitle: 'variants.title',
      variantSku: 'variants.sku',
      variantBarcode: 'variants.barcode',
      tag: 'tag',
      body: 'body',
      author: 'author'
    };

    return Object.keys(fieldsMap)
      .filter((key) => config.fields[key])
      .map((key) => fieldsMap[key]);
  }

  function queryUrl(query) {
    const resourceTypes = getResourceTypes();
    if (!config.enabled || !resourceTypes.length) return null;

    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', resourceTypes.join(','));
    url.searchParams.set('resources[limit]', String(config.limit));
    url.searchParams.set('resources[limit_scope]', config.limitScope);
    url.searchParams.set('resources[options][unavailable_products]', config.unavailableProducts);

    const searchFields = getSearchFields();
    if (searchFields.length) {
      url.searchParams.set('resources[options][fields]', searchFields.join(','));
    }

    return url;
  }

  function buildSearchUrl(query) {
    const searchUrl = new URL(window.A24SearchRoutingConfig?.searchUrl || `${rootPath}search`, window.location.origin);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('q', `${query}*`);
    searchUrl.searchParams.set('options[prefix]', 'last');
    searchUrl.searchParams.set('options[unavailable_products]', config.unavailableProducts);
    return searchUrl.toString();
  }

  function buildAllProductsUrl(query) {
    const collectionUrl = new URL(window.A24SearchRoutingConfig?.allProductsUrl || `${rootPath}collections/all`, window.location.origin);
    collectionUrl.searchParams.set('q', query);
    return collectionUrl.toString();
  }

  function buildAllResultsUrl(query) {
    return config.allResultsTarget === 'all_products' ? buildAllProductsUrl(query) : buildSearchUrl(query);
  }

  function sanitizeStyledText(value) {
    return String(value || '')
      .replace(/<(?!\/?mark\b)[^>]*>/gi, '')
      .replace(/<mark\b[^>]*>/gi, '<mark>');
  }

  function getSuggestionLabel(item, query) {
    if (item?.styled_text) return sanitizeStyledText(item.styled_text);
    const text = item?.text || '';
    if (!item?.generated) return escapeHtml(text);
    return highlightSuggestionText(text, query);
  }

  function stripHtml(value) {
    return String(value || '').replace(/<[^>]*>/g, ' ');
  }

  function normalizeForCompare(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function cleanSuggestionText(value) {
    return stripHtml(value)
      .replace(/[–—]/g, ' ')
      .replace(/\s*[|•]+\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^[\s:;,.\-/]+|[\s:;,.\-/]+$/g, '');
  }

  function highlightSuggestionText(text, query) {
    const value = String(text || '');
    const search = String(query || '').trim();
    if (!search) return escapeHtml(value);

    const index = value.toLowerCase().indexOf(search.toLowerCase());
    if (index < 0) return escapeHtml(value);

    return `${escapeHtml(value.slice(0, index))}<mark>${escapeHtml(value.slice(index, index + search.length))}</mark>${escapeHtml(value.slice(index + search.length))}`;
  }

  function addGeneratedSuggestion(map, text, query, score) {
    const cleanText = cleanSuggestionText(text);
    const compareText = normalizeForCompare(cleanText);
    const compareQuery = normalizeForCompare(query);
    if (!cleanText || !compareQuery || compareText.length <= compareQuery.length || !compareText.startsWith(compareQuery)) return;

    const existing = map.get(compareText);
    if (!existing || existing.score < score) {
      map.set(compareText, { text: cleanText, score });
    }
  }

  function addGeneratedSuggestionsFromText(map, text, query, score) {
    const cleanText = cleanSuggestionText(text);
    const compareQuery = normalizeForCompare(query);
    if (!cleanText || !compareQuery) return;

    const words = cleanText
      .replace(/[()\[\]{}]/g, ' ')
      .replace(/[–—]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);

    words.forEach((word, index) => {
      if (!normalizeForCompare(word).startsWith(compareQuery)) return;
      [1, 2, 3].forEach((wordCount, wordCountIndex) => {
        const phrase = words.slice(index, index + wordCount).join(' ');
        addGeneratedSuggestion(map, phrase, query, score - wordCountIndex);
      });
    });
  }

  function createGeneratedQuerySuggestions(results, query, nativeQueries) {
    if (!config.showQueries || !config.generateQuerySuggestions) return [];

    const nativeKeys = new Set((nativeQueries || []).map((item) => normalizeForCompare(item.text || item.styled_text || '')));
    const suggestions = new Map();
    const addText = (text, score) => addGeneratedSuggestionsFromText(suggestions, text, query, score);

    (results.collections || []).forEach((item) => {
      addText(item.title, 90);
    });

    (results.products || []).forEach((item) => {
      addGeneratedSuggestion(suggestions, item.vendor, query, 80);
      addText(item.title, 70);
      if (item.vendor && item.type) addGeneratedSuggestion(suggestions, `${item.vendor} ${item.type}`, query, 75);
    });

    (results.pages || []).forEach((item) => addText(item.title, 45));
    (results.articles || []).forEach((item) => addText(item.title, 40));

    return Array.from(suggestions.values())
      .filter((item) => !nativeKeys.has(normalizeForCompare(item.text)))
      .sort((a, b) => b.score - a.score || a.text.length - b.text.length || a.text.localeCompare(b.text, 'de'))
      .slice(0, config.generatedQueryLimit)
      .map((item) => ({ text: item.text, generated: true }));
  }

  function getImageUrl(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || image.preview_image?.url || image.preview_image?.src || '';
  }

  function formatPrice(value) {
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  function renderSection(title, items, renderer, query) {
    if (!items.length) return '';
    return `<div class="search-dropdown__section">${escapeHtml(title)}</div>${items.map((item) => renderer(item, query)).join('')}`;
  }

  function renderQuery(item, currentQuery) {
    const query = normalizeQuery(item.text || '');
    const url = query ? buildSearchUrl(query) : item.url;
    return `<a href="${escapeHtml(url)}" class="search-dropdown__query" role="option"><span>${getSuggestionLabel(item, currentQuery)}</span></a>`;
  }

  function renderProduct(item) {
    const image = getImageUrl(item.image || item.featured_image);
    const imageHtml = image ? `<img src="${escapeHtml(image)}" alt="" width="44" height="44" loading="lazy">` : '';
    const price = formatPrice(item.price);
    const vendor = item.vendor ? `<span class="search-dropdown__meta">${escapeHtml(item.vendor)}</span>` : '';
    return `<a href="${escapeHtml(item.url)}" class="search-dropdown__product" role="option"><div class="search-dropdown__thumb">${imageHtml}</div><div class="search-dropdown__info"><span class="search-dropdown__name">${escapeHtml(item.title || '')}</span>${vendor}${price ? `<span class="search-dropdown__price">${escapeHtml(price)}</span>` : ''}</div></a>`;
  }

  function renderCollection(item) {
    const image = getImageUrl(item.image || item.featured_image);
    const imageHtml = image ? `<span class="search-dropdown__thumb--sm"><img src="${escapeHtml(image)}" alt="" width="32" height="32" loading="lazy"></span>` : '';
    return `<a href="${escapeHtml(item.url)}" class="search-dropdown__query" role="option">${imageHtml}<span>${escapeHtml(item.title || '')}</span></a>`;
  }

  function renderPage(item) {
    return `<a href="${escapeHtml(item.url)}" class="search-dropdown__query" role="option"><span>${escapeHtml(item.title || '')}</span></a>`;
  }

  function renderArticle(item) {
    const image = getImageUrl(item.image);
    const imageHtml = image ? `<span class="search-dropdown__thumb--sm"><img src="${escapeHtml(image)}" alt="" width="32" height="32" loading="lazy"></span>` : '';
    const author = item.author ? `<span class="search-dropdown__meta">${escapeHtml(item.author)}</span>` : '';
    return `<a href="${escapeHtml(item.url)}" class="search-dropdown__article" role="option">${imageHtml}<span class="search-dropdown__info"><span class="search-dropdown__name">${escapeHtml(item.title || '')}</span>${author}</span></a>`;
  }

  function createHtml(results, query) {
    const nativeQueries = config.showQueries ? (results.queries || []) : [];
    const generatedQueries = createGeneratedQuerySuggestions(results, query, nativeQueries);
    const queries = config.showQueries ? nativeQueries.concat(generatedQueries) : [];
    const products = config.showProducts ? (results.products || []) : [];
    const collections = config.showCollections ? (results.collections || []) : [];
    const pages = config.showPages ? (results.pages || []) : [];
    const articles = config.showArticles ? (results.articles || []) : [];

    if (!products.length && !queries.length && !collections.length && !pages.length && !articles.length) return '';

    let html = '';
    html += renderSection('Suchvorschläge', queries, renderQuery, query);
    html += renderSection('Kollektionen', collections, renderCollection, query);
    html += renderSection('Produkte', products, renderProduct, query);
    html += renderSection('Seiten', pages, renderPage, query);
    html += renderSection('Blogartikel', articles, renderArticle, query);

    if (config.showAllResultsLink) {
      html += `<a href="${escapeHtml(buildAllResultsUrl(query))}" class="search-dropdown__all" role="option">Alle Ergebnisse für »${escapeHtml(query)}« →</a>`;
    }

    return html;
  }

  function init(inputId, dropdownId, wrapId) {
    const input = document.getElementById(inputId);
    const dropdown = document.getElementById(dropdownId);
    const wrapper = document.getElementById(wrapId) || input?.closest('.search-wrap');
    if (!input || !dropdown || input.dataset.a24PredictiveBound === 'true') return;
    input.dataset.a24PredictiveBound = 'true';
    let timer;
    let controller;
    let activeIndex = -1;

    const options = () => Array.from(dropdown.querySelectorAll('a[role="option"]'));
    const ensureOptionIds = () => {
      options().forEach((link, itemIndex) => {
        if (!link.id) link.id = `${dropdownId}-option-${itemIndex}`;
        link.setAttribute('aria-selected', 'false');
      });
    };
    const clearActive = () => {
      options().forEach((link) => {
        link.classList.remove('is-active');
        link.setAttribute('aria-selected', 'false');
      });
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    };
    const close = () => {
      dropdown.innerHTML = '';
      dropdown.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    };
    const setActive = (index) => {
      const links = options();
      if (!links.length) {
        clearActive();
        return;
      }

      const nextIndex = Math.min(Math.max(index, 0), links.length - 1);
      links.forEach((link, itemIndex) => {
        const isActive = itemIndex === nextIndex;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      activeIndex = nextIndex;
      input.setAttribute('aria-activedescendant', links[nextIndex].id);
      links[nextIndex].scrollIntoView({ block: 'nearest' });
    };
    const getNavigationIndex = () => {
      if (activeIndex >= 0) return activeIndex;
      return options().indexOf(document.activeElement);
    };
    const handleNavigationKeydown = (event) => {
      const links = options();
      if (!links.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const currentIndex = getNavigationIndex();
        setActive(currentIndex >= links.length - 1 ? 0 : currentIndex + 1);
        input.focus({ preventScroll: true });
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const currentIndex = getNavigationIndex();
        setActive(currentIndex <= 0 ? links.length - 1 : currentIndex - 1);
        input.focus({ preventScroll: true });
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        input.focus({ preventScroll: true });
      }

      if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        links[activeIndex]?.click();
      }
    };
    const load = async (query) => {
      const url = queryUrl(query);
      if (!url) return close();

      controller?.abort();
      controller = new AbortController();
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Predictive Search HTTP ' + response.status);
        const payload = await response.json();
        if (normalizeQuery(input.value) !== query) return;
        const html = createHtml(payload?.resources?.results || {}, query);
        if (!html) return close();
        dropdown.innerHTML = html;
        dropdown.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
        ensureOptionIds();
        clearActive();
      } catch (error) {
        if (error.name !== 'AbortError') close();
      }
    };

    input.addEventListener('input', () => {
      const query = normalizeQuery(input.value);
      clearTimeout(timer);
      if (!config.enabled || query.length < config.minChars) return close();
      timer = window.setTimeout(() => load(query), config.debounceMs);
    });
    input.addEventListener('keydown', handleNavigationKeydown);
    dropdown.addEventListener('keydown', handleNavigationKeydown);
    dropdown.addEventListener('mouseover', (event) => {
      const link = event.target.closest?.('a[role="option"]');
      if (!link || !dropdown.contains(link)) return;
      const index = options().indexOf(link);
      if (index >= 0) setActive(index);
    });
    document.addEventListener('click', (event) => { if (wrapper && !wrapper.contains(event.target)) close(); });
  }

  const start = () => fields.forEach((ids) => init(...ids));
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
}());
